import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  Row,
  Col,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addBadFitResolutionReason,
  getBadFitResolutionReasonDataById,
  updateBadFitResolutionReason,
} from "model/BadFitResolutionReasonModel";

const initialFormData = {
  reason: "",
  description: "",
  status: true,
};

const badFitResolutionReasonFormSchema = Yup.object().shape({
  reason: Yup.string().trim().required("Reason is required"),
  description: Yup.string().trim(),
  status: Yup.boolean().required("Status is required"),
});

const BadFitResolutionReasonAddEditModal = ({
  setBadFitResolutionReasonAddEditModalOpen,
  badFitResolutionReasonAddEditModalOpen,
  getBadFitResolutionReasonList,
  itemId,
  setItemId,
}) => {
  const [formKey, setFormKey] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const updateFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" || type === "switch" ? checked : value;
    updateFormField(name, fieldValue);
  };

  const closeModal = () => {
    setBadFitResolutionReasonAddEditModalOpen(false);
    resetModalFormState();
    setItemId?.(null);
  };

  const resetModalFormState = () => {
    setFormData(initialFormData);
    setErrors({});
    setFormKey((prev) => prev + 1);
  };

  const validateForm = async () => {
    try {
      await badFitResolutionReasonFormSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      return false;
    }
  };

  useEffect(() => {
    if (!badFitResolutionReasonAddEditModalOpen) return;

    let isCancelled = false;

    const loadModalData = async () => {
      setLoading(true);
      try {
        if (!itemId) {
          resetModalFormState();
          return;
        }

        const response = await getBadFitResolutionReasonDataById({ Id: itemId });
        if (isCancelled || !response?.data) return;

        const row = response.data;
        setFormData({
          reason: row?.reason || "",
          description: row?.description || "",
          status: row?.status === "active",
        });
        setErrors({});
        setFormKey((prev) => prev + 1);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadModalData();

    return () => {
      isCancelled = true;
    };
  }, [badFitResolutionReasonAddEditModalOpen, itemId]);

  useEffect(() => {
    if (!badFitResolutionReasonAddEditModalOpen) {
      resetModalFormState();
      setItemId?.(null);
    }
  }, [badFitResolutionReasonAddEditModalOpen]);

  const handleSave = async () => {
    const isFormValid = await validateForm();
    if (!isFormValid) return;

    const payload = {
      reason: formData.reason.trim(),
      description: formData.description.trim(),
      status: formData.status ? "active" : "deactive",
    };

    const response = itemId
      ? await updateBadFitResolutionReason(itemId, payload)
      : await addBadFitResolutionReason(payload);

    if (response?.success) {
      toast.success(response?.message || "Bad fit resolution reason saved successfully");
      getBadFitResolutionReasonList?.();
      closeModal();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleSave();
  };

  return (
    <Modal
      isOpen={badFitResolutionReasonAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      centered
      zIndex={5000}
      className="modal-full-height"
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "92vh", overflowY: "auto", backgroundColor: "#f8fafc" }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="fw-bold mb-1">
              {itemId ? "Edit" : "Create"} Bad Fit Resolution Reason
            </h5>
          </div>
          <Button close onClick={closeModal} />
        </div>

        <Form onSubmit={handleFormSubmit}>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>
                      Reason <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                    />
                    {errors?.reason && (
                      <small className="text-danger">{errors.reason}</small>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      name="description"
                      rows="4"
                      value={formData.description}
                      onChange={handleChange}
                    />
                    {errors?.description && (
                      <small className="text-danger">{errors.description}</small>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>
                      Status <span className="text-danger">*</span>
                    </Label>
                    <div className="input-box d-flex align-items-center justify-content-between">
                      <span className="text-muted">Type</span>
                      <div className="d-flex align-items-center">
                        <strong className="me-2">
                          {formData?.status ? "Active" : "Deactive"}
                        </strong>
                        <div className="form-switch">
                          <Input
                            key={`status-${formKey}-${formData?.status}`}
                            type="switch"
                            name="status"
                            defaultChecked={formData?.status}
                            onChange={handleChange}
                            className="form-check-input-padding"
                          />
                        </div>
                      </div>
                    </div>
                    {errors?.status && (
                      <small className="text-danger">{errors.status}</small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>

          <div className="text-end mt-4">
            <Button color="primary" type="submit" disabled={loading}>
              Save
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default BadFitResolutionReasonAddEditModal;
